import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Users, Calendar, Award, Target, Heart, Plus, Trash2, Phone, Building2, MessageCircle, Mail, Copy, Check } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { membershipAPI } from '../services/api';
import { toast } from 'sonner';

// Payment Details Component
const PaymentDetailsCard = ({ amount }) => {
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-green-800 text-xl">Payment Options</CardTitle>
        <p className="text-sm text-green-700">Amount to pay: <span className="font-bold text-lg">{amount}</span></p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Mobile Money */}
        <div>
          <h4 className="font-semibold text-slate-800 flex items-center mb-3">
            <Phone className="w-5 h-5 mr-2 text-yellow-600" />
            Mobile Money
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="font-semibold text-yellow-800">MTN Mobile Money</p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Number:</span>
                  <button 
                    onClick={() => copyToClipboard('0555439160', 'mtn')}
                    className="flex items-center text-yellow-700 font-mono hover:text-yellow-900"
                  >
                    0555439160
                    {copiedField === 'mtn' ? <Check className="w-4 h-4 ml-1" /> : <Copy className="w-4 h-4 ml-1" />}
                  </button>
                </div>
                <p className="text-sm text-slate-600">Name: <span className="font-medium">Ralph Benjamin Teiko Quaye</span></p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-semibold text-red-800">Telecel Cash</p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Number:</span>
                  <button 
                    onClick={() => copyToClipboard('0201358015', 'telecel')}
                    className="flex items-center text-red-700 font-mono hover:text-red-900"
                  >
                    0201358015
                    {copiedField === 'telecel' ? <Check className="w-4 h-4 ml-1" /> : <Copy className="w-4 h-4 ml-1" />}
                  </button>
                </div>
                <p className="text-sm text-slate-600">Name: <span className="font-medium">Ralph Benjamin Teiko Quaye</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Transfer */}
        <div>
          <h4 className="font-semibold text-slate-800 flex items-center mb-3">
            <Building2 className="w-5 h-5 mr-2 text-blue-600" />
            Bank Transfer
          </h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-semibold text-blue-800">Access Bank Ghana Limited</p>
            <div className="mt-2 space-y-1">
              <p className="text-sm text-slate-600">Account Name: <span className="font-medium">Streamline Aquatics Limited</span></p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Account Number:</span>
                <button 
                  onClick={() => copyToClipboard('0320223680231', 'bank')}
                  className="flex items-center text-blue-700 font-mono hover:text-blue-900"
                >
                  0320223680231
                  {copiedField === 'bank' ? <Check className="w-4 h-4 ml-1" /> : <Copy className="w-4 h-4 ml-1" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Send Proof */}
        <div className="bg-slate-100 rounded-lg p-4">
          <h4 className="font-semibold text-slate-800 mb-2">After Payment</h4>
          <p className="text-sm text-slate-600 mb-3">
            Send proof of payment (screenshot/receipt) via:
          </p>
          <div className="flex flex-wrap gap-3">
            <a 
              href="https://wa.me/233201358015?text=Hi%2C%20I%20just%20made%20a%20payment%20for%20Marlins%20Swim%20Team%20registration.%20Here%20is%20my%20proof%20of%20payment."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp: +233 20 135 8015
            </a>
            <a 
              href="mailto:info@streamlineaquatics.co?subject=Payment%20Proof%20-%20Marlins%20Team%20Registration"
              className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors text-sm"
            >
              <Mail className="w-4 h-4 mr-2" />
              info@streamlineaquatics.co
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SwimTeam = ({ user, onLogout }) => {
  const [swimmers, setSwimmers] = useState([{
    id: 1,
    swimmer_name: '',
    swimmer_email: '',
    swimmer_phone: '',
    swimmer_age: '',
    swimmer_dob: '',
  }]);

  const [formData, setFormData] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    membership_type: 'monthly',
    start_date: '',
  });

  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);

  const addSwimmer = () => {
    setSwimmers([...swimmers, {
      id: swimmers.length + 1,
      swimmer_name: '',
      swimmer_email: '',
      swimmer_phone: '',
      swimmer_age: '',
      swimmer_dob: '',
    }]);
  };

  const removeSwimmer = (id) => {
    if (swimmers.length > 1) {
      setSwimmers(swimmers.filter(s => s.id !== id));
    }
  };

  const updateSwimmer = (id, field, value) => {
    setSwimmers(swimmers.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const calculatePricing = () => {
    const registrationFee = 700 * swimmers.length;
    let subscriptionPerSwimmer = 0;
    let savingsText = '';

    switch(formData.membership_type) {
      case 'annual':
        subscriptionPerSwimmer = 10800;
        savingsText = ' (Save GHS 1,200/year)';
        break;
      case 'monthly':
        subscriptionPerSwimmer = 1000;
        break;
      case 'quarterly':
        subscriptionPerSwimmer = 2850;
        savingsText = ' (Save GHS 600/year)';
        break;
      case 'half_yearly':
        subscriptionPerSwimmer = 5400;
        savingsText = ' (Save GHS 1,200/year)';
        break;
      default:
        subscriptionPerSwimmer = 1000;
    }

    let subscriptionTotal = subscriptionPerSwimmer * swimmers.length;

    // Apply family discounts
    if (swimmers.length === 2) {
      subscriptionTotal = subscriptionPerSwimmer + (subscriptionPerSwimmer * 0.9);
    } else if (swimmers.length >= 3) {
      subscriptionTotal = subscriptionPerSwimmer + (subscriptionPerSwimmer * 0.9) + 
                         (subscriptionPerSwimmer * 0.85 * (swimmers.length - 2));
    }

    const totalAmount = Math.round((registrationFee + subscriptionTotal) * 100); // Convert to pesewas

    return {
      registrationFee,
      subscriptionPerSwimmer,
      subscriptionTotal: Math.round(subscriptionTotal),
      totalAmount,
      savingsText,
      display: `GHS ${Math.round(registrationFee + subscriptionTotal).toLocaleString()}`
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const pricing = calculatePricing();
      const swimmerNames = [];
      
      // Create membership for each swimmer
      for (const swimmer of swimmers) {
        await membershipAPI.create({
          swimmer_name: swimmer.swimmer_name,
          swimmer_email: swimmer.swimmer_email || formData.parent_email,
          swimmer_phone: swimmer.swimmer_phone || formData.parent_phone,
          swimmer_age: parseInt(swimmer.swimmer_age),
          date_of_birth: swimmer.swimmer_dob || null,
          parent_name: formData.parent_name,
          parent_email: formData.parent_email,
          parent_phone: formData.parent_phone,
          membership_type: formData.membership_type,
          start_date: formData.start_date,
          amount: pricing.totalAmount,
        });
        swimmerNames.push(swimmer.swimmer_name);
      }

      setRegistrationSuccess({
        swimmerNames,
        membershipType: formData.membership_type,
        amount: pricing.display
      });
      
      toast.success('Registration submitted successfully!');
      resetForm();
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSwimmers([{
      id: 1,
      swimmer_name: '',
      swimmer_email: '',
      swimmer_phone: '',
      swimmer_age: '',
      swimmer_dob: '',
    }]);
    setFormData({
      parent_name: '',
      parent_email: '',
      parent_phone: '',
      membership_type: 'monthly',
      start_date: '',
    });
  };

  // Show success page with payment instructions
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="pt-32 pb-20">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-white shadow-xl border-2 border-orange-200">
                <CardHeader className="text-center bg-orange-50">
                  <div className="w-16 h-16 bg-orange-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Trophy className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-orange-800">Welcome to the Marlins Family!</CardTitle>
                  <p className="text-orange-700 mt-2">
                    Registration submitted successfully. Please make payment to confirm enrollment.
                  </p>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-2">Registration Details</h4>
                    <p className="text-sm text-slate-600">
                      Swimmer(s): <span className="font-medium">{registrationSuccess.swimmerNames.join(', ')}</span>
                    </p>
                    <p className="text-sm text-slate-600">
                      Membership: <span className="font-medium capitalize">{registrationSuccess.membershipType}</span>
                    </p>
                  </div>

                  <PaymentDetailsCard amount={registrationSuccess.amount} />

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800 mb-2">After Payment Confirmation</h4>
                    <p className="text-sm text-slate-600">
                      Once payment is verified, we will contact you to arrange:
                    </p>
                    <ul className="text-sm text-slate-600 mt-2 list-disc list-inside">
                      <li>Uniform fitting and collection (T-shirt & swim cap)</li>
                      <li>Training schedule assignment</li>
                      <li>Coach introduction</li>
                    </ul>
                  </div>

                  <div className="text-center pt-4">
                    <Button 
                      onClick={() => setRegistrationSuccess(null)}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      Register Another Swimmer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation user={user} onLogout={onLogout} />

      {/* Hero with Marlins Logo */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-orange-50 via-cyan-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <img 
              src="/marlins-logo.svg"
              alt="Marlins Swim Team"
              className="h-32 w-auto mx-auto mb-6"
            />
            <h1 className="text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">Join the Marlins</h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Ghana&apos;s premier competitive swim team. Where champions are made and dreams become reality.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What Makes Us Special</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              More than just a swim team - we are a family committed to excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Expert Coaching',
                description: 'Train with nationally recognized coaches who have developed Olympic hopefuls',
              },
              {
                icon: Calendar,
                title: 'Year-Round Training',
                description: 'Consistent training schedule with competitions throughout the year',
              },
              {
                icon: Users,
                title: 'Team Community',
                description: 'Build lasting friendships with fellow swimmers who share your passion',
              },
              {
                icon: Award,
                title: 'Competitive Success',
                description: 'Regular participation in regional and national competitions',
              },
              {
                icon: Heart,
                title: 'Character Building',
                description: 'Develop discipline, resilience, and leadership skills',
              },
              {
                icon: Trophy,
                title: 'Professional Training',
                description: 'Train at Achimota Swimming & Leisure Center with quality equipment',
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-orange-200 transition-all shadow-lg"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Schedule */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Training Schedule</h2>
            <p className="text-xl text-slate-600">Marlins train year-round at Achimota Swimming & Leisure Center</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 border-2 border-orange-200 shadow-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <Calendar className="w-6 h-6 mr-2 text-orange-600" />
                Weekday Training
              </h3>
              <div className="space-y-3 text-slate-700">
                <p><strong>Monday - Friday:</strong> 5:30 AM - 7:00 AM</p>
                <p><strong>Tuesday - Friday:</strong> 4:30 PM - 6:00 PM</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-cyan-200 shadow-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <Calendar className="w-6 h-6 mr-2 text-cyan-600" />
                Weekend Training
              </h3>
              <div className="space-y-3 text-slate-700">
                <p><strong>Saturday:</strong> 10:00 AM - 12:00 PM</p>
                <p><strong>Saturday:</strong> 3:00 PM - 5:00 PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Register Today</h2>
            <p className="text-xl text-slate-600">Begin your journey to competitive excellence</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-orange-50 to-cyan-50 rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-orange-200"
          >
            <form onSubmit={(e) => handleSubmit(e, true)} className="space-y-8">
              {/* Parent Info */}
              <div className="border-b border-orange-200 pb-6">
                <h3 className="text-2xl font-bold mb-4 text-orange-700 flex items-center">
                  <Users className="w-6 h-6 mr-2" />
                  Parent/Guardian Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="parent_name">Full Name *</Label>
                    <Input
                      id="parent_name"
                      value={formData.parent_name}
                      onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
                      required
                      className="bg-white"
                      data-testid="parent-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="parent_email">Email *</Label>
                    <Input
                      id="parent_email"
                      type="email"
                      value={formData.parent_email}
                      onChange={(e) => setFormData({...formData, parent_email: e.target.value})}
                      required
                      className="bg-white"
                      data-testid="parent-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="parent_phone">Phone *</Label>
                    <Input
                      id="parent_phone"
                      value={formData.parent_phone}
                      onChange={(e) => setFormData({...formData, parent_phone: e.target.value})}
                      placeholder="+233 XX XXX XXXX"
                      required
                      className="bg-white"
                      data-testid="parent-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Swimmers */}
              <div className="border-b border-cyan-200 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-cyan-700 flex items-center">
                    <Trophy className="w-6 h-6 mr-2" />
                    Swimmer(s) Information
                  </h3>
                  <Button
                    type="button"
                    onClick={addSwimmer}
                    variant="outline"
                    className="border-cyan-600 text-cyan-700 hover:bg-cyan-50"
                    data-testid="add-swimmer-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Swimmer
                  </Button>
                </div>

                {swimmers.map((swimmer, index) => (
                  <div key={swimmer.id} className="mb-6 p-6 bg-white rounded-xl border-2 border-slate-100">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-slate-900">Swimmer {index + 1}</h4>
                      {swimmers.length > 1 && (
                        <Button
                          type="button"
                          onClick={() => removeSwimmer(swimmer.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`remove-swimmer-${index}-btn`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`swimmer_name_${swimmer.id}`}>Swimmer Name *</Label>
                        <Input
                          id={`swimmer_name_${swimmer.id}`}
                          value={swimmer.swimmer_name}
                          onChange={(e) => updateSwimmer(swimmer.id, 'swimmer_name', e.target.value)}
                          required
                          data-testid={`swimmer-name-${index}-input`}
                        />
                      </div>

                      <div>
                        <Label htmlFor={`swimmer_age_${swimmer.id}`}>Age *</Label>
                        <Input
                          id={`swimmer_age_${swimmer.id}`}
                          type="number"
                          value={swimmer.swimmer_age}
                          onChange={(e) => updateSwimmer(swimmer.id, 'swimmer_age', e.target.value)}
                          required
                          data-testid={`swimmer-age-${index}-input`}
                        />
                      </div>

                      <div>
                        <Label htmlFor={`swimmer_dob_${swimmer.id}`}>Date of Birth</Label>
                        <Input
                          id={`swimmer_dob_${swimmer.id}`}
                          type="date"
                          value={swimmer.swimmer_dob}
                          onChange={(e) => updateSwimmer(swimmer.id, 'swimmer_dob', e.target.value)}
                          data-testid={`swimmer-dob-${index}-input`}
                        />
                      </div>

                      <div>
                        <Label htmlFor={`swimmer_email_${swimmer.id}`}>Email (Optional)</Label>
                        <Input
                          id={`swimmer_email_${swimmer.id}`}
                          type="email"
                          value={swimmer.swimmer_email}
                          onChange={(e) => updateSwimmer(swimmer.id, 'swimmer_email', e.target.value)}
                          placeholder="Optional"
                          data-testid={`swimmer-email-${index}-input`}
                        />
                      </div>

                      <div>
                        <Label htmlFor={`swimmer_phone_${swimmer.id}`}>Phone (Optional)</Label>
                        <Input
                          id={`swimmer_phone_${swimmer.id}`}
                          value={swimmer.swimmer_phone}
                          onChange={(e) => updateSwimmer(swimmer.id, 'swimmer_phone', e.target.value)}
                          placeholder="Optional"
                          data-testid={`swimmer-phone-${index}-input`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Membership Details */}
              <div className="border-b border-orange-200 pb-6">
                <h3 className="text-2xl font-bold mb-4 text-orange-700">Membership Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="membership_type">Payment Plan *</Label>
                    <Select
                      value={formData.membership_type}
                      onValueChange={(value) => setFormData({...formData, membership_type: value})}
                    >
                      <SelectTrigger className="bg-white" data-testid="membership-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-50 bg-white">
                        <SelectItem value="monthly">Monthly (GHS 1,000/month)</SelectItem>
                        <SelectItem value="quarterly">Quarterly (GHS 2,850/quarter - Save GHS 600/year)</SelectItem>
                        <SelectItem value="half_yearly">Half-Yearly (GHS 5,400/half-year - Save GHS 1,200/year)</SelectItem>
                        <SelectItem value="annual">Annual (GHS 10,800/year - Save GHS 1,200/year)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      className="bg-white"
                      data-testid="start-date-input"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="bg-white rounded-xl p-6 border-2 border-cyan-600 shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-cyan-700">Payment Summary</h3>
                <div className="space-y-3 text-slate-700">
                  <div className="flex justify-between">
                    <span>Registration Fee ({swimmers.length} swimmer{swimmers.length > 1 ? 's' : ''}):</span>
                    <span className="font-semibold">GHS {calculatePricing().registrationFee}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Subscription ({formData.membership_type}){calculatePricing().savingsText}:</span>
                    <span className="font-semibold">GHS {calculatePricing().subscriptionTotal}</span>
                  </div>
                  {swimmers.length > 1 && (
                    <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
                      Family discount applied: {swimmers.length === 2 ? '10% on 2nd child' : '10% on 2nd, 15% on 3rd+ children'}
                    </div>
                  )}
                  <div className="border-t-2 border-slate-200 pt-3 flex justify-between items-center">
                    <span className="text-xl font-bold">Total Amount:</span>
                    <span className="text-3xl font-bold text-orange-600">{calculatePricing().display}</span>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  * Registration fee includes Marlins T-Shirt and Swim Cap
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full py-6 text-lg font-semibold shadow-xl"
                  disabled={loading}
                  data-testid="submit-membership-btn"
                >
                  {loading ? 'Processing...' : 'Submit Registration'}
                </Button>
              </div>
              
              <p className="text-sm text-slate-600 text-center">
                * After submitting, you will see payment details to complete your registration.
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">Marlins Team FAQs</h2>
          
          <div className="space-y-6">
            {[
              {
                q: 'How do I join the Marlins swim team?',
                a: 'Swimmers must be able to swim at least 25 meters unassisted and be committed to regular training. We conduct assessments to place swimmers in the appropriate training group.',
              },
              {
                q: 'What is the training schedule?',
                a: 'The Marlins train multiple times per week. Training schedule: Mon-Fri 5:30am-7:00am, Tue-Fri 4:30pm-6:00pm, Saturdays 10am-12pm and 3pm-5pm.',
              },
              {
                q: 'Do team members compete?',
                a: 'Yes! We participate in local, regional, and national competitions throughout the year. Competition is optional but highly encouraged for skill development.',
              },
              {
                q: 'What equipment do team members need?',
                a: 'Team members need a competition swimsuit, goggles, and training fins. The Marlins T-shirt and swim cap are included with registration. We can help you source quality equipment.',
              },
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-lg border-2 border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SwimTeam;
